/**
 * Lightweight SSE (Server-Sent Events) client for streaming AI responses.
 */

export interface SSECallbacks {
  /** Called for each text chunk received */
  onChunk: (text: string) => void;
  /** Called when the stream completes. Metadata is present for coach streams. */
  onDone: (metadata?: Record<string, unknown>) => void;
  /** Called on error */
  onError: (error: Error) => void;
  /** Optional AbortSignal for cancellation */
  signal?: AbortSignal;
}

const API_BASE = "/api";

/**
 * Stream a chat response via SSE.
 * Makes a POST request with `?stream=true` and reads the SSE event stream.
 */
export async function streamChat(
  endpoint: string,
  body: Record<string, unknown>,
  token: string,
  callbacks: SSECallbacks,
): Promise<void> {
  const separator = endpoint.includes("?") ? "&" : "?";
  const url = `${API_BASE}${endpoint}${separator}stream=true`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal: callbacks.signal,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || "Stream request failed");
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6);

        if (data === "[DONE]") {
          callbacks.onDone();
          return;
        }

        try {
          const parsed = JSON.parse(data);

          if (typeof parsed === "string") {
            // Simple text chunk (activity chat, global chat)
            callbacks.onChunk(parsed);
          } else if (parsed.type === "text") {
            // Typed text chunk (coach chat)
            callbacks.onChunk(parsed.content);
          } else if (parsed.type === "done") {
            // Final metadata event (coach chat with suggestion)
            callbacks.onDone(parsed);
            return;
          } else if (parsed.type === "error") {
            callbacks.onError(new Error(parsed.error));
            return;
          }
        } catch {
          // Skip malformed lines
        }
      }
    }

    // Stream ended without [DONE] — treat as complete
    callbacks.onDone();
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return;
    callbacks.onError(err instanceof Error ? err : new Error("Stream failed"));
  }
}
